import { useState, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useGlobalVariable,
  useUpdateGlobalVariable
} from '@api/enterprise/globalVariable';
import { globalVariablesKeys } from '@api/enterprise/globalVariable/constants';
import type { ChecklistItem, ChecklistFormData } from '../types';
import {
  parseChecklistData,
  stringifyChecklistData,
  addChecklistItem,
  updateChecklistItem,
  deleteChecklistItem,
  sortBySequence,
  resequenceAllItems,
  addItemAtPosition
} from '../utils';

const VARIABLE_NAME = 'HIDE_PRECAL_CHECKLIST';

export const useChecklistData = () => {
  const [localItems, setLocalItems] = useState<ChecklistItem[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const queryClient = useQueryClient();

  const {
    data: globalVariable,
    isLoading,
    error,
    refetch
  } = useGlobalVariable(VARIABLE_NAME);
  const updateMutation = useUpdateGlobalVariable();

  const originalItems = useMemo(() => {
    if (!globalVariable?.blobText) return [];
    return sortBySequence(parseChecklistData(globalVariable.blobText));
  }, [globalVariable?.blobText]);

  useEffect(() => {
    setLocalItems(originalItems);
    setHasUnsavedChanges(false);
  }, [originalItems]);

  const addItem = async (formData: ChecklistFormData) => {
    const updatedItems = addChecklistItem(localItems, formData);
    const resequencedItems = resequenceAllItems(updatedItems);
    setLocalItems(resequencedItems);

    try {
      const jsonString = stringifyChecklistData(resequencedItems);

      await updateMutation.mutateAsync({
        name: VARIABLE_NAME,
        command: {
          blobText: jsonString,
          text: globalVariable?.text || null,
          int: globalVariable?.int || null,
          date: globalVariable?.date || null,
          numeric: globalVariable?.numeric || null
        }
      });

      await queryClient.invalidateQueries({
        queryKey: globalVariablesKeys.byName(VARIABLE_NAME)
      });

      await refetch();
      setHasUnsavedChanges(false);
      return { success: true };
    } catch (error) {
      setHasUnsavedChanges(true);
      throw error;
    }
  };

  const addItemAtPositionAction = async (
    formData: ChecklistFormData,
    position: number
  ) => {
    const updatedItems = addItemAtPosition(localItems, formData, position);
    setLocalItems(updatedItems);

    try {
      const jsonString = stringifyChecklistData(updatedItems);

      await updateMutation.mutateAsync({
        name: VARIABLE_NAME,
        command: {
          blobText: jsonString,
          text: globalVariable?.text || null,
          int: globalVariable?.int || null,
          date: globalVariable?.date || null,
          numeric: globalVariable?.numeric || null
        }
      });

      await queryClient.invalidateQueries({
        queryKey: globalVariablesKeys.byName(VARIABLE_NAME)
      });

      await refetch();
      setHasUnsavedChanges(false);
      return { success: true };
    } catch (error) {
      setHasUnsavedChanges(true);
      throw error;
    }
  };

  const updateItem = async (id: string, updates: Partial<ChecklistItem>) => {
    const updatedItems = updateChecklistItem(localItems, id, updates);
    const resequencedItems = resequenceAllItems(updatedItems);
    setLocalItems(resequencedItems);

    try {
      const jsonString = stringifyChecklistData(resequencedItems);

      await updateMutation.mutateAsync({
        name: VARIABLE_NAME,
        command: {
          blobText: jsonString,
          text: globalVariable?.text || null,
          int: globalVariable?.int || null,
          date: globalVariable?.date || null,
          numeric: globalVariable?.numeric || null
        }
      });

      await queryClient.invalidateQueries({
        queryKey: globalVariablesKeys.byName(VARIABLE_NAME)
      });

      await refetch();
      setHasUnsavedChanges(false);
    } catch (error) {
      setHasUnsavedChanges(true);
      throw error;
    }
  };

  const deleteItem = async (id: string) => {
    const updatedItems = deleteChecklistItem(localItems, id);
    const resequencedItems = resequenceAllItems(updatedItems);
    setLocalItems(resequencedItems);

    try {
      const jsonString = stringifyChecklistData(resequencedItems);

      await updateMutation.mutateAsync({
        name: VARIABLE_NAME,
        command: {
          blobText: jsonString,
          text: globalVariable?.text || null,
          int: globalVariable?.int || null,
          date: globalVariable?.date || null,
          numeric: globalVariable?.numeric || null
        }
      });

      await queryClient.invalidateQueries({
        queryKey: globalVariablesKeys.byName(VARIABLE_NAME)
      });

      await refetch();
      setHasUnsavedChanges(false);
    } catch (error) {
      setHasUnsavedChanges(true);
      throw error;
    }
  };

  const reorderItems = (reorderedItems: ChecklistItem[]) => {
    setLocalItems(reorderedItems);
    setHasUnsavedChanges(true);
  };

  const saveChanges = async () => {
    try {
      const jsonString = stringifyChecklistData(localItems);

      const result = await updateMutation.mutateAsync({
        name: VARIABLE_NAME,
        command: {
          blobText: jsonString,
          text: globalVariable?.text || null,
          int: globalVariable?.int || null,
          date: globalVariable?.date || null,
          numeric: globalVariable?.numeric || null
        }
      });

      await queryClient.invalidateQueries({
        queryKey: globalVariablesKeys.byName(VARIABLE_NAME)
      });

      await refetch();

      setHasUnsavedChanges(false);

      return result;
    } catch (error) {
      console.error('Failed to save checklist:', error);
      throw error;
    }
  };

  const discardChanges = () => {
    setLocalItems(originalItems);
    setHasUnsavedChanges(false);
  };

  const refresh = () => {
    refetch();
  };

  return {
    items: localItems,
    originalItems,
    isLoading,
    error,
    hasUnsavedChanges,
    isSaving: updateMutation.isPending,
    saveError: updateMutation.error,
    actions: {
      addItem,
      addItemAtPosition: addItemAtPositionAction,
      updateItem,
      deleteItem,
      reorderItems,
      saveChanges,
      discardChanges,
      refresh
    }
  };
};
